import styled, { keyframes, css } from 'styled-components'
import { theme } from '../../../theme'

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const EditorContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 20px;
  width: 400px;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  z-index: 1000;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    max-height: 100vh;
    margin-top: 0;
    border-radius: 0;
    z-index: 9999;
    padding: 20px;
    display: flex;
    flex-direction: column;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c4c4c4;
    border-radius: 3px;

    &:hover {
      background: #a0a0a0;
    }
  }
`

export const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  @media (max-width: 768px) {
    padding-bottom: 16px;
    margin-bottom: 24px;
    border-bottom: 2px solid #e5e7eb;

    h3 {
      font-size: 18px;
    }
  }
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 28px;
  }
`

export const TagsList = styled.div`
  margin-bottom: 24px;
  flex-shrink: 0;

  h4 {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`

export const TagItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    transform: translateX(2px);
  }
`

export const TagColor = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const TagName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
`

export const TagType = styled.span<{ $isPrimary?: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  ${({ $isPrimary }) =>
    $isPrimary
      ? css`
          background: #dbeafe;
          color: #1e40af;
        `
      : css`
          background: #f3f4f6;
          color: #6b7280;
        `}
`

export const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  opacity: 0.6;

  &:hover {
    opacity: 1;
    background: #fee2e2;
  }
`

export const SearchSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  h4 {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  margin-bottom: 12px;

  &:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

export const ExistingTagsList = styled.div`
  max-height: 180px;
  overflow-y: auto;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  flex-shrink: 1;

  @media (max-width: 768px) {
    flex: 1;
    min-height: 200px;
    max-height: none;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 2px;
  }
`

export const ExistingTagItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 4px;

  ${({ $selected }) =>
    $selected
      ? css`
          background: #dbeafe;
          border: 1px solid #60a5fa;
        `
      : css`
          background: transparent;
          border: 1px solid transparent;

          &:hover {
            background: #f9fafb;
          }
        `}
`

export const ExistingTagColor = styled.div<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const ExistingTagName = styled.span`
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
`

export const NewTagSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  h4 {
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }
`

export const TagInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`

export const ColorPicker = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

export const ColorOption = styled.div<{ $color: string; $selected: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${({ $color }) => $color};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  ${({ $selected }) =>
    $selected &&
    css`
      box-shadow:
        0 0 0 2px white,
        0 0 0 4px #60a5fa;
      transform: scale(1.1);
    `}

  &:hover {
    transform: scale(1.1);
  }
`

export const TagTypeToggle = styled.div`
  margin-bottom: 16px;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #4b5563;
    cursor: pointer;

    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      cursor: pointer;
    }
  }
`

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: 12px;
    padding-top: 12px;
  }
`

export const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  outline: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 14px;
  }
`

export const AddButton = styled(Button)`
  background: #10b981;
  color: white;

  &:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  }
`

export const SaveButton = styled(Button)`
  background: #3b82f6;
  color: white;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }
`

export const CancelButton = styled(Button)`
  background: #f3f4f6;
  color: #4b5563;

  &:hover {
    background: #e5e7eb;
  }
`

export const CreateNewButton = styled(Button)`
  background: white;
  color: #3b82f6;
  border: 1px solid #3b82f6;

  &:hover {
    background: #eff6ff;
  }
`

export const WarningMessage = styled.div`
  padding: 8px 12px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  color: #92400e;
  font-size: 13px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚠️';
  }
`

export const EmptyMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  font-style: italic;
`

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  margin: 20px auto;
`

export const MobileBackdrop = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
    animation: ${fadeIn} 0.2s ease;
  }
`
